/**
 * A date typed rather than clicked.
 *
 * The calendar is quick for a day near the one it opens on and slow for
 * anything else: its arrows move a month at a time, so a production from two
 * years ago was twenty-three clicks to reach (measured 2026-09-26 — "17 Oct
 * 2024" then "1/1/2028" took forty-three clicks between them). Typing it is
 * one.
 *
 * **Day first, always.** Wingspace is UK-first and so is the person typing:
 * "1/2/2026" is the first of February. A month written as a word can sit
 * either side of the day ("17 Oct 2024", "Oct 17 2024"), because with a word
 * there is no ambiguity to resolve. A missing year is this year; a two-digit
 * year is this century up to '69, as people mean it.
 *
 * Returns null for anything it can't read as one real date — never a guess,
 * and never a rolled-over one: "31/2/2026" is nothing, not 3 March.
 */

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

const WEEKDAYS = /\b(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)(?:day|nesday|rsday|urday)?\b\.?/g

interface Parts {
  day: number
  month: number
  /** Null when none was typed — filled in from today, or from the other end of a span. */
  year: number | null
}

function monthFromWord(word: string): number | null {
  const clean = word.replace(/\.$/, '')
  if (clean.length < 3) return null
  const index = MONTHS.findIndex((name) => name.startsWith(clean))
  return index === -1 ? null : index + 1
}

function fullYear(text: string): number {
  const value = Number(text)
  if (text.length === 4) return value
  return value < 70 ? 2000 + value : 1900 + value
}

function readParts(raw: string): Parts | null {
  const text = raw
    .toLowerCase()
    .replace(WEEKDAYS, ' ')
    .replace(/(\d+)(?:st|nd|rd|th)\b/g, '$1')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return null

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text)
  if (iso) return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) }

  const numeric = /^(\d{1,2})[/.\- ](\d{1,2})(?:[/.\- ](\d{2}|\d{4}))?$/.exec(text)
  if (numeric) {
    return { day: Number(numeric[1]), month: Number(numeric[2]), year: numeric[3] ? fullYear(numeric[3]) : null }
  }

  // A month as a word, with the day either side of it.
  const tokens = text.split(/[\s/.-]+/).filter(Boolean)
  const monthAt = tokens.findIndex((token) => /^[a-z]/.test(token) && monthFromWord(token) !== null)
  if (monthAt === -1) return null

  const numbers = tokens.filter((_, index) => index !== monthAt)
  if (numbers.some((token) => !/^\d+$/.test(token))) return null

  const day = numbers.find((token) => token.length <= 2)
  if (!day) return null
  const year = numbers.find((token) => token !== day && (token.length === 2 || token.length === 4))
  if (numbers.length > (year ? 2 : 1)) return null

  return { day: Number(day), month: monthFromWord(tokens[monthAt]) as number, year: year ? fullYear(year) : null }
}

function toDate(parts: Parts, fallbackYear: number): Date | null {
  const year = parts.year ?? fallbackYear
  const date = new Date(year, parts.month - 1, parts.day)
  const real = date.getFullYear() === year && date.getMonth() === parts.month - 1 && date.getDate() === parts.day
  return real ? date : null
}

/** One date, or null. */
export function parseTypedDate(text: string, today: Date = new Date()): Date | null {
  const parts = readParts(text)
  return parts ? toDate(parts, today.getFullYear()) : null
}

// "to", or a dash standing between two dates. A hyphen only counts with space
// around it, so "17-10-2024" stays one date; an en or em dash always counts.
const SPAN_SEPARATOR = /\s+(?:to|until|till)\s+|\s+-\s+|\s*[–—]\s*/

/**
 * Two dates written as a span — "17/10/2024 – 1/1/2028", "3 to 7 Nov" is not
 * one, but "3 Nov to 7 Nov" is. When only one end has a year, the other takes
 * it, so "17 Oct – 3 Nov 2024" is autumn 2024 rather than this year's October.
 * Returned in order whichever end was typed first. A single date comes back as
 * a span of one day, so a range field accepts either.
 */
export function parseTypedSpan(text: string, today: Date = new Date()): { start: Date; end: Date } | null {
  const halves = text.split(SPAN_SEPARATOR).filter((half) => half.trim() !== '')
  if (halves.length === 1) {
    const one = parseTypedDate(halves[0], today)
    return one ? { start: one, end: one } : null
  }
  if (halves.length !== 2) return null

  const [a, b] = halves.map(readParts)
  if (!a || !b) return null
  const year = a.year ?? b.year ?? today.getFullYear()
  const first = toDate(a, a.year ?? year)
  const second = toDate(b, b.year ?? year)
  if (!first || !second) return null

  return first <= second ? { start: first, end: second } : { start: second, end: first }
}
